import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  Paper,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Tooltip,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';

import PromptHistoryModal from './PromptHistoryModal';
import AgentFormBasicInfo from './AgentFormBasicInfo';
import AgentFormPromptSection from './AgentFormPromptSection';
import AgentFormModelSettings from './AgentFormModelSettings';
import AgentFormAdvancedSettings from './AgentFormAdvancedSettings';
import { AI_PROVIDERS, getImageFieldsConfig } from '../constants/aiModels';

const initialExpandedState = {
  basicInfo: true,
  prompt: true,
  modelSettings: true,
  advancedSettings: false,
};

const fetchSpecificAgent = async (agentIdToFetch) => {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config`);
    if (!res.ok) {
      let errorMsg = `Error ${res.status} al obtener lista de agentes.`;
      try { 
        const errorBody = await res.json();
        errorMsg = errorBody.message || errorMsg;
      } catch(e) {}
      throw new Error(errorMsg);
    }
    const allAgents = await res.json();
    const updatedAgentData = (allAgents || []).find(a => a.agent_id === agentIdToFetch);
    if (!updatedAgentData) {
      throw new Error(`No se encontró el agente actualizado con ID: ${agentIdToFetch}`);
    }
    return updatedAgentData;
  } catch (error) {
    console.error("Error fetching specific agent data:", error);
    throw error;
  }
};

function AgentForm({ agent, fetchAgents, onSaved, isAgentLoading, onFormReady }) {
  const [formData, setFormData] = useState({
    agentId: "",
    description: "",
    prompt: "",
    temperature: 0.7,
    maxTokens: 150,
    topP: 0.9,
    providerAi: "",
    modelAi: "",
    project: "default",
    metadataStr: "{}",
    mcpConfigStr: "null",
    imageDimension: "",
    aspectRatio: "",
    imageSize: "",
    extraParameters: {}
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [metadataError, setMetadataError] = useState(null);
  const [mcpConfigError, setMcpConfigError] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [revertSuccessMessage, setRevertSuccessMessage] = useState(null);
  const [expanded, setExpanded] = useState(initialExpandedState);

  const availableModels = useMemo(() => {
    if (!formData.providerAi) return [];
    const provider = AI_PROVIDERS.find(p => p.name.toLowerCase() === formData.providerAi.toLowerCase());
    return provider ? provider.models : [];
  }, [formData.providerAi]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded({
      ...expanded,
      [panel]: isExpanded
    });
  };

  const handleFieldChange = (value, fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
    if (fieldName === 'metadataStr') setMetadataError(null);
    if (fieldName === 'mcpConfigStr') setMcpConfigError(null);
  };
  
  const handleAutocompleteChange = (value, fieldName) => {
    if (fieldName === 'extra_parameters') {
      const updates = {};
      
      if (value.resolution !== undefined) {
        updates.imageDimension = value.resolution;
      }
      if (value.aspect_ratio !== undefined) {
        updates.aspectRatio = value.aspect_ratio;
      }
      if (value.image_size !== undefined) {
        updates.imageSize = value.image_size;
      }
      
      setFormData(prev => ({
        ...prev,
        ...updates,
        extraParameters: { ...prev.extraParameters, ...value }
      }));
      return;
    }
    
    const newValue = value || ""; 
    setFormData(prev => ({ ...prev, [fieldName]: newValue }));
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
    if (fieldName === 'providerAi') {
      setFormData(prev => ({ ...prev, modelAi: "" })); 
      if (errors.modelAi) {
        setErrors(prev => ({ ...prev, modelAi: null }));
      }
      
      const provider = newValue?.toLowerCase();
      if (provider === 'openai') {
        setFormData(prev => {
          const newExtraParams = { ...prev.extraParameters };
          delete newExtraParams.aspect_ratio;
          delete newExtraParams.image_size;
          if (prev.imageDimension) {
            newExtraParams.resolution = prev.imageDimension;
          }
          return {
            ...prev,
            aspectRatio: "",
            imageSize: "",
            extraParameters: newExtraParams
          };
        });
      } else if (provider === 'gemini') {
        setFormData(prev => {
          const newExtraParams = { ...prev.extraParameters };
          delete newExtraParams.resolution;
          if (prev.aspectRatio) {
            newExtraParams.aspect_ratio = prev.aspectRatio;
          }
          if (prev.imageSize) {
            newExtraParams.image_size = prev.imageSize;
          }
          return {
            ...prev,
            imageDimension: "",
            extraParameters: newExtraParams
          };
        });
      } else {
        setFormData(prev => {
          const newExtraParams = { ...prev.extraParameters };
          delete newExtraParams.resolution;
          delete newExtraParams.aspect_ratio;
          delete newExtraParams.image_size;
          return {
            ...prev,
            imageDimension: "",
            aspectRatio: "",
            imageSize: "",
            extraParameters: newExtraParams
          };
        });
      }
    }
  };

  const resetForm = (currentAgent) => {
    const extraParams = currentAgent?.preferences?.extra_parameters || {};
    
    setFormData({
      agentId: currentAgent?.agent_id || "",
      description: currentAgent?.description || "",
      prompt: currentAgent?.prompt || "",
      temperature: currentAgent?.preferences?.temperature ?? 0.7,
      maxTokens: currentAgent?.preferences?.max_tokens ?? 150,
      topP: currentAgent?.preferences?.top_p ?? 0.9,
      providerAi: currentAgent?.provider_ai || "",
      modelAi: currentAgent?.model_ai || "",
      project: currentAgent?.project || "default",
      metadataStr: currentAgent?.metadata ? JSON.stringify(currentAgent.metadata, null, 2) : "{}",
      mcpConfigStr: currentAgent?.mcp_config ? JSON.stringify(currentAgent.mcp_config, null, 2) : "null",
      imageDimension: extraParams.resolution || "",
      aspectRatio: extraParams.aspect_ratio || "",
      imageSize: extraParams.image_size || "",
      extraParameters: extraParams
    });
    
    setMetadataError(null);
    setMcpConfigError(null);
    setResult(null);
    setErrors({});
    setExpanded({ ...initialExpandedState, advancedSettings: !currentAgent }); 
    setHistoryModalOpen(false);
    setPromptHistory([]);
    setHistoryError(null);
    setRevertSuccessMessage(null);
    
    if (onFormReady) {
      setTimeout(() => onFormReady(), 0); 
    }
  };

  useEffect(() => {
    resetForm(agent);
  }, [agent]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.agentId.trim()) newErrors.agentId = "El Agent ID es obligatorio.";
    if (!formData.description.trim()) newErrors.description = "La Descripción es obligatoria.";
    if (!formData.prompt.trim()) newErrors.prompt = "El Prompt es obligatorio.";
    if (!formData.providerAi.trim()) newErrors.providerAi = "El Proveedor AI es obligatorio.";
    if (!formData.modelAi.trim()) newErrors.modelAi = "El Modelo AI es obligatorio.";
    if (!formData.project.trim()) newErrors.project = "El Proyecto es obligatorio.";
    
    const imageFieldsConfig = getImageFieldsConfig(formData.agentId, formData.providerAi);
    
    if (imageFieldsConfig.showFields) {
      if (imageFieldsConfig.provider === 'openai') {
        if (!formData.imageDimension || !formData.imageDimension.trim()) {
          newErrors.imageDimension = "La dimensión de la imagen es obligatoria.";
        }
      } else if (imageFieldsConfig.provider === 'gemini') {
        if (!formData.aspectRatio || !formData.aspectRatio.trim()) {
          newErrors.aspectRatio = "El Aspect Ratio es obligatorio.";
        }
        if (!formData.imageSize || !formData.imageSize.trim()) {
          newErrors.imageSize = "El Image Size es obligatorio.";
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const safeJsonParse = (jsonString, defaultValue) => {
    if (!jsonString || typeof jsonString !== 'string' || jsonString.trim().toLowerCase() === 'null' || jsonString.trim() === '') {
      return defaultValue;
    }
    try {
      return JSON.parse(jsonString);
    } catch (err) {
      throw new Error(`JSON inválido: ${err.message}`);
    }
  };

  const handleOpenHistory = async () => {
    if (!formData.agentId) return;
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    setPromptHistory([]); 
    setRevertSuccessMessage(null); 

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config/${formData.agentId}/prompts/history`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `Error ${response.status}` }));
        throw new Error(errorData.message || `Error ${response.status}`);
      }
      const data = await response.json();
      setPromptHistory(data || []);
    } catch (error) {
      console.error("Error al cargar historial de prompts:", error);
      setHistoryError(`Error al cargar historial: ${error.message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRevertPrompt = async (historyId) => {
    const agentIdToRevert = formData.agentId; 
    if (!agentIdToRevert || !historyId) return;

    setRevertSuccessMessage(null);
    
    let revertError = null;

    try {
      const revertResponse = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config/${agentIdToRevert}/prompts/revert/${historyId}`, {
        method: 'PUT'
      });

      if (!revertResponse.ok) {
        const errorData = await revertResponse.json().catch(() => ({ message: `Error ${revertResponse.status}` }));
        throw new Error(errorData.message || `Error ${revertResponse.status} al revertir`); 
      }

      if (fetchAgents) {
         fetchAgents().catch(err => console.error("Error refrescando lista post-revert (ignorado):", err));
      }
      
      const updatedAgentData = await fetchSpecificAgent(agentIdToRevert);
      const extraParams = updatedAgentData.preferences?.extra_parameters || {};

      setFormData({
        agentId: updatedAgentData.agent_id,
        description: updatedAgentData.description,
        prompt: updatedAgentData.prompt,
        temperature: updatedAgentData.preferences?.temperature ?? 0.7,
        maxTokens: updatedAgentData.preferences?.max_tokens ?? 150,
        topP: updatedAgentData.preferences?.top_p ?? 0.9,
        providerAi: updatedAgentData.provider_ai,
        modelAi: updatedAgentData.model_ai,
        project: updatedAgentData.project || "default",
        metadataStr: updatedAgentData.metadata ? JSON.stringify(updatedAgentData.metadata, null, 2) : "{}",
        mcpConfigStr: updatedAgentData.mcp_config ? JSON.stringify(updatedAgentData.mcp_config, null, 2) : "null",
        imageDimension: extraParams.resolution || "",
        aspectRatio: extraParams.aspect_ratio || "",
        imageSize: extraParams.image_size || "",
        extraParameters: extraParams
      });
      setErrors({});
      setMetadataError(null);
      setMcpConfigError(null);

      setHistoryModalOpen(false);
      setRevertSuccessMessage('¡Prompt revertido y formulario actualizado!');

      if (onSaved) {
          onSaved(updatedAgentData);
      }

    } catch (error) {
      console.error("Error en el proceso de revertir o refrescar:", error);
      if (!historyModalOpen) {
          setResult({ error: `Error al refrescar datos después de revertir: ${error.message}` });
      } else {
          throw error; 
      }
    }
  };
  
  useEffect(() => {
    if (revertSuccessMessage) {
      const timer = setTimeout(() => {
        setRevertSuccessMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [revertSuccessMessage]);

  const handleSubmitAgent = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMetadataError(null);
    setMcpConfigError(null);
    setErrors({});

    if (!validateForm()) {
      setLoading(false);
      const errorFields = Object.keys(errors);
      if (errorFields.includes('agentId') || errorFields.includes('description') || errorFields.includes('project')) {
        setExpanded(prev => ({ ...prev, basicInfo: true }));
      }
      if (errorFields.includes('prompt')) {
        setExpanded(prev => ({ ...prev, prompt: true }));
      }
      if (errorFields.includes('providerAi') || errorFields.includes('modelAi') || 
          errorFields.includes('imageDimension') || errorFields.includes('aspectRatio') || 
          errorFields.includes('imageSize')) {
        setExpanded(prev => ({ ...prev, modelSettings: true }));
      }
      return;
    }

    let parsedMetadata;
    let parsedMcpConfig;
    try {
      parsedMetadata = safeJsonParse(formData.metadataStr, {});
    } catch (err) {
      setMetadataError(err.message);
      setExpanded(prev => ({ ...prev, advancedSettings: true }));
      setLoading(false);
      return;
    }
    try {
      parsedMcpConfig = safeJsonParse(formData.mcpConfigStr, null);
    } catch (err) {
      setMcpConfigError(err.message);
      setExpanded(prev => ({ ...prev, advancedSettings: true }));
      setLoading(false);
      return;
    }

    const dataToSend = {
      agent_id: formData.agentId,
      description: formData.description,
      prompt: formData.prompt,
      preferences: {
        extra_parameters: formData.extraParameters,
        temperature: formData.temperature,
        max_tokens: formData.maxTokens,
        top_p: formData.topP
      },
      provider_ai: formData.providerAi,
      model_ai: formData.modelAi,
      project: formData.project,
      metadata: parsedMetadata,
      mcp_config: parsedMcpConfig,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL_FLUXI}/api/ms/agent/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend)
      });
      const resultJson = await response.json();
      setResult(resultJson);

      if (response.ok) {
         if (fetchAgents) fetchAgents();
         if (onSaved) onSaved(resultJson);
      } else {
        setResult({ error: `Error ${response.status}: ${resultJson.message || 'Error desconocido'}` });
      }
     
    } catch (error) {
      setResult({ error: "Error de red o conexión: " + error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmitAgent} noValidate sx={{ position: 'relative' }}>
      {isAgentLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(230, 230, 230, 0.6)', 
            zIndex: 10, 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: 1 
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <fieldset disabled={isAgentLoading} style={{ border: 'none', padding: 0, margin: 0 }}>
        <Accordion 
          expanded={expanded.basicInfo} 
          onChange={handleAccordionChange('basicInfo')}
          sx={{ mb: 2, ...(errors.agentId || errors.description || errors.project ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="basic-info-content"
            id="basic-info-header"
          >
            <Typography variant="h6">Información Básica</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormBasicInfo 
              agentId={formData.agentId}
              project={formData.project}
              description={formData.description}
              errors={errors}
              onFieldChange={handleFieldChange}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded.prompt} 
          onChange={handleAccordionChange('prompt')}
          sx={{ mb: 2, ...(errors.prompt ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="prompt-content"
            id="prompt-header"
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6">Prompt</Typography>
              {agent && (
                <Tooltip title="Ver historial de prompts">
                  <IconButton onClick={handleOpenHistory} size="small" sx={{ ml: 1 }}>
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormPromptSection 
              prompt={formData.prompt}
              errors={errors}
              onFieldChange={handleFieldChange}
              revertSuccessMessage={revertSuccessMessage}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded.modelSettings} 
          onChange={handleAccordionChange('modelSettings')}
          sx={{ mb: 2, ...(errors.providerAi || errors.modelAi ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="model-settings-content"
            id="model-settings-header"
          >
            <Typography variant="h6">Configuración del Modelo</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormModelSettings 
              providerAi={formData.providerAi}
              modelAi={formData.modelAi}
              temperature={formData.temperature}
              maxTokens={formData.maxTokens}
              topP={formData.topP}
              errors={errors}
              availableModels={availableModels} 
              aiProviders={AI_PROVIDERS}
              onFieldChange={handleFieldChange}
              onAutocompleteChange={handleAutocompleteChange}
              imageDimension={formData.imageDimension}
              aspectRatio={formData.aspectRatio}
              imageSize={formData.imageSize}
              agentId={formData.agentId}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          expanded={expanded.advancedSettings} 
          onChange={handleAccordionChange('advancedSettings')}
          sx={{ mb: 2, ...(metadataError || mcpConfigError ? { border: '1px solid red' } : {}) }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="advanced-settings-content"
            id="advanced-settings-header"
          >
            <Typography variant="h6">Configuración Avanzada</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AgentFormAdvancedSettings 
              metadataStr={formData.metadataStr}
              mcpConfigStr={formData.mcpConfigStr}
              metadataError={metadataError}
              mcpConfigError={mcpConfigError}
              onFieldChange={handleFieldChange}
            />
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            size="large"
            disabled={loading || isAgentLoading} 
            sx={{ minWidth: 200 }}
          >
            {loading ? ( 
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Guardando...
              </>
            ) : (
              "Guardar Agente"
            )}
          </Button>
        </Box>

      </fieldset>
      
      {result && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={result.error ? "error" : "success"}>
            {result.error ? result.error : "Agente guardado correctamente."}
          </Alert>
          {!result.error && (
            <Paper sx={{ p: 1, mt: 1, backgroundColor: "#f0f0f0", maxHeight: '150px', overflow: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '0.8rem' }}>{JSON.stringify(result, null, 2)}</pre>
            </Paper>
          )}
        </Box>
      )}

      <PromptHistoryModal 
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        agentId={formData.agentId}
        historyData={promptHistory}
        isLoading={historyLoading}
        onRevert={handleRevertPrompt}
      />
    </Box>
  );
}

export default AgentForm;
